// store/admin/certificates/store/_saga.js
import { call, put, takeLatest, all } from 'redux-saga/effects';
// import { console } from 'react-consoleify';
import * as types from './_actionTypes';
import * as actions from './_actions';
import CertificateHelper from '../../../../utils/API/BackendHelper/admin/certificate';

// =====================================================
// FETCH CERTIFICATES
// =====================================================
function* fetchCertificatesSaga(action) {
  try {
    const response = yield call(CertificateHelper.getCertificates, action.payload);
    
    if (response.success) {
      yield put(actions.fetchCertificatesSuccess(response.data));
    } else {
      throw new Error(response.message || 'Failed to fetch certificates');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch certificates';
    yield put(actions.fetchCertificatesFailure(errorMessage));
    console.error(errorMessage);
  }
}

// =====================================================
// FETCH CERTIFICATE BY ID
// =====================================================
function* fetchCertificateByIdSaga(action) {
  try {
    const response = yield call(CertificateHelper.getCertificateById, action.payload);
    
    if (response.success) {
      yield put(actions.fetchCertificateByIdSuccess(response.data));
    } else {
      throw new Error(response.message || 'Failed to fetch certificate');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch certificate';
    yield put(actions.fetchCertificateByIdFailure(errorMessage));
    console.error(errorMessage);
  }
}

// =====================================================
// FETCH PENDING APPROVALS
// =====================================================
function* fetchPendingApprovalsSaga(action) {
  try {
    const response = yield call(CertificateHelper.getPendingApprovals, action.payload);
    console.log('[CertificateSaga] pendingApprovals raw response:', response);

    if (response.success) {
      console.log('[CertificateSaga] pendingApprovals data:', response.data);
      yield put(actions.fetchPendingApprovalsSuccess(response.data));
    } else {
      throw new Error(response.message || 'Failed to fetch pending approvals');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch pending approvals';
    yield put(actions.fetchPendingApprovalsFailure(errorMessage));
    console.error(errorMessage);
  }
}

// =====================================================
// FETCH CERTIFICATE STATS
// =====================================================
function* fetchCertificateStatsSaga(action) {
  try {
    const response = yield call(CertificateHelper.getCertificateStats, action.payload);
    
    if (response.success) {
      yield put(actions.fetchCertificateStatsSuccess(response.data));
    } else {
      throw new Error(response.message || 'Failed to fetch statistics');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch statistics';
    yield put(actions.fetchCertificateStatsFailure(errorMessage));
  }
}

// =====================================================
// APPROVE CERTIFICATE (Single)
// =====================================================
function* approveCertificateSaga(action) {
  try {
    const response = yield call(CertificateHelper.approveCertificate, action.payload);
    
    if (response.success) {
      yield put(actions.approveCertificateSuccess(response.data));
      console.success(response.message || 'Certificate approved successfully');
    } else {
      throw new Error(response.message || 'Failed to approve certificate');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to approve certificate';
    yield put(actions.approveCertificateFailure(errorMessage));
    console.error(errorMessage);
  }
}

// =====================================================
// ISSUE MANUAL CERTIFICATE
// =====================================================
function* issueManualCertificateSaga(action) {
  try {
    const response = yield call(CertificateHelper.issueManualCertificate, action.payload);

    if (response.success) {
      // Status 200 means a certificate already existed; 201 means newly created
      const alreadyExists = response.status === 200;
      yield put(actions.issueManualCertificateSuccess({
        certificate: response.data,
        alreadyExists,
        message: response.message,
      }));
    } else {
      throw new Error(response.message || 'Failed to issue manual certificate');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to issue manual certificate';
    yield put(actions.issueManualCertificateFailure(errorMessage));
    console.error(errorMessage);
  }
}

// =====================================================
// BULK APPROVE CERTIFICATES
// =====================================================
function* bulkApproveCertificatesSaga(action) {
  try {
    const response = yield call(CertificateHelper.bulkApproveCertificates, action.payload);
    
    if (response.success) {
      yield put(actions.bulkApproveCertificatesSuccess(response.data));
      console.success(response.message || 'Certificates approved successfully');
    } else {
      throw new Error(response.message || 'Failed to bulk approve certificates');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to bulk approve certificates';
    yield put(actions.bulkApproveCertificatesFailure(errorMessage));
    console.error(errorMessage);
  }
}

// =====================================================
// REVOKE CERTIFICATE
// =====================================================
function* revokeCertificateSaga(action) {
  try {
    const { certificateId, reason } = action.payload;
    const response = yield call(CertificateHelper.revokeCertificate, certificateId, { reason });
    
    if (response.success) {
      yield put(actions.revokeCertificateSuccess(response.data));
      console.success(response.message || 'Certificate revoked successfully');
    } else {
      throw new Error(response.message || 'Failed to revoke certificate');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to revoke certificate';
    yield put(actions.revokeCertificateFailure(errorMessage));
    console.error(errorMessage);
  }
}

// =====================================================
// VERIFY CERTIFICATE
// =====================================================
function* verifyCertificateSaga(action) {
  try {
    const response = yield call(CertificateHelper.verifyCertificate, action.payload);
    
    yield put(actions.verifyCertificateSuccess(response.data));
    
    if (response.data?.isValid) {
      console.success('Certificate is valid');
    } else {
      console.warning(response.data?.message || 'Certificate verification failed');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Verification failed';
    yield put(actions.verifyCertificateFailure(errorMessage));
    console.error(errorMessage);
  }
}

// =====================================================
// FETCH COLLEGES
// =====================================================
function* fetchCollegesSaga() {
  try {
    const response = yield call(CertificateHelper.getColleges);
    
    if (response.success) {
      yield put(actions.fetchCollegesSuccess(response.data));
    } else {
      throw new Error(response.message || 'Failed to fetch colleges');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch colleges';
    yield put(actions.fetchCollegesFailure(errorMessage));
  }
}

// =====================================================
// FETCH COURSES
// =====================================================
function* fetchCoursesSaga() {
  try {
    const response = yield call(CertificateHelper.getCourses);
    
    if (response.success) {
      yield put(actions.fetchCoursesSuccess(response.data));
    } else {
      throw new Error(response.message || 'Failed to fetch courses');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch courses';
    yield put(actions.fetchCoursesFailure(errorMessage));
  }
}

// =====================================================
// ROOT SAGA
// =====================================================
function* adminCertificatesSaga() {
  yield all([
    takeLatest(types.FETCH_CERTIFICATES_REQUEST, fetchCertificatesSaga),
    takeLatest(types.FETCH_CERTIFICATE_BY_ID_REQUEST, fetchCertificateByIdSaga),
    takeLatest(types.FETCH_PENDING_APPROVALS_REQUEST, fetchPendingApprovalsSaga),
    takeLatest(types.FETCH_CERTIFICATE_STATS_REQUEST, fetchCertificateStatsSaga),
    takeLatest(types.APPROVE_CERTIFICATE_REQUEST, approveCertificateSaga),
    takeLatest(types.ISSUE_MANUAL_CERTIFICATE_REQUEST, issueManualCertificateSaga),
    takeLatest(types.BULK_APPROVE_CERTIFICATES_REQUEST, bulkApproveCertificatesSaga),
    takeLatest(types.REVOKE_CERTIFICATE_REQUEST, revokeCertificateSaga),
    takeLatest(types.VERIFY_CERTIFICATE_REQUEST, verifyCertificateSaga),
    takeLatest(types.FETCH_COLLEGES_REQUEST, fetchCollegesSaga),
    takeLatest(types.FETCH_COURSES_REQUEST, fetchCoursesSaga)
  ]);
}

export default adminCertificatesSaga;